import { Alert, Box, Button, FormControl, InputLabel, Link, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Backend } from "../services/Backend";
import { useState } from "react";
import { SuccessToast } from "../utils/utils";
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email address').required('Email address is required'),
    reason: Yup.string().required('Reason for deletion is required').min(10, 'Please provide a detailed reason (at least 10 characters)'),
});

export function DeleteAccountPage() {
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [email, setEmail] = useState("");
const [reason, setReason] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateForm = async () => {
        try {
            await validationSchema.validate({
                email,
                reason,
            });
            setError("");
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        }
    }

    const onSubmitDeleteRequest = async () => {
        if (!await validateForm()) return;
        
      
        setIsSubmitting(true);
        setError("");

        const body = {
            email,
            reason,
        };

        try {
            await Backend.post("delete-account-request", body);
            setSuccess("Your account deletion request has been submitted successfully. We will review your request and contact you within 3-5 business days.");
            setEmail("");
            setReason("");
            SuccessToast("Delete request submitted successfully");
        } catch (err) {
            setError(err.message || "Failed to submit delete request. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <div className="w-[500px] mx-[auto] mt-[100px] mb-[50px]">
            <Typography variant="h4" component="h1" className="text-center mb-[30px] text-red-600">
                Request Account Deletion
            </Typography>
            
            <Typography variant="body1" className="text-center mb-[30px] text-gray-600">
                We're sorry to see you go. Please provide your email address and reason for deletion. 
                We will review your request and contact you within 3-5 business days.
            </Typography>

            {error && <Alert severity="error" className="mb-[20px]">{error}</Alert>}
            {success && <Alert severity="success" className="mb-[20px]">{success}</Alert>}

            <Box component="form" sx={{ mt: 1 }}>
                <TextField 
                    margin="normal" 
                    required 
                    fullWidth 
                    label="Email Address" 
                    type="email"
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                />

                <FormControl fullWidth margin="normal">
                    <InputLabel>Reason for Deletion</InputLabel>
                    <Select
                        label="Reason for Deletion"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={isSubmitting}
                    >
                        <MenuItem value="No longer using the service">No longer using the service</MenuItem>
                        <MenuItem value="Privacy concerns">Privacy concerns</MenuItem>
                        <MenuItem value="Unsatisfied with the service">Unsatisfied with the service</MenuItem>
                        <MenuItem value="Duplicate account">Duplicate account</MenuItem>
                        <MenuItem value="Company policy">Company policy</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </Select>
                </FormControl>

                {reason === "Other" && (
                    <TextField 
                        margin="normal" 
                        required 
                        fullWidth 
                        label="Please specify your reason" 
                        multiline
                        rows={4}
                        value={reason === "Other" ? "" : reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={isSubmitting}
                        placeholder="Please provide a detailed explanation for your account deletion request..."
                    />
                )}

                <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    sx={{ mt: 3, mb: 2 }}
                    onClick={onSubmitDeleteRequest}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Submitting Request..." : "Submit Delete Request"}
                </Button>

                <Typography variant="body2" className="text-center mt-[20px] text-gray-500">
                    <strong>Note:</strong> This action will submit a request for account deletion. 
                    Your account will remain active until we process your request. 
                    You may cancel this request by contacting our support team.
                </Typography>
            </Box>
        </div>
    );
}